import { useForm } from '@tanstack/react-form';
import { createFileRoute, Link, Navigate } from '@tanstack/react-router';
import { Check, Info, Loader2, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { loginSchema } from 'shared';
import { toast } from 'sonner';
import { AuthPage } from '@/components/features/auth/auth-page';
import { Field, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field';
import { useTheme } from '@/contexts/theme-provider';
import {
  buildBackendUrlFromTenant,
  clearTenantId,
  createDynamicAuthClient,
  getBackendUrl,
  getTenantId,
  setBackendUrl,
  setDeviceToken,
} from '@/lib/better-auth';
import { isNativePlatform } from '@/lib/native-features';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useAuth } from '../contexts/auth-context';

export const Route = createFileRoute('/login')({
  component: Login,
});

function Login() {
  const { t } = useTranslation();
  const { isAuthenticated, clearBackendUrl, setNativeUser } = useAuth();
  const [isLoading, setIsLoading] = useState(false);

  // Tenant configuration state (separate from sign-in form)
  const [tenantIdInput, setTenantIdInput] = useState('');
  const [isValidatingBackend, setIsValidatingBackend] = useState(false);
  const [backendValidationResult, setBackendValidationResult] = useState<
    'success' | 'error' | null
  >(null);
  const [savedTenantId, setSavedTenantId] = useState<string>('');
  const [isAdvancedMode, setIsAdvancedMode] = useState(false);

  // Load saved tenant ID on mount
  useEffect(() => {
    const saved = getTenantId();
    if (saved) {
      setSavedTenantId(saved);
      setTenantIdInput(saved);
    }
  }, []);

  const form = useForm({
    defaultValues: {
      identifier: '',
      password: '',
    },
    onSubmit: async ({ value }) => {
      // Validate login credentials first
      const validationResult = loginSchema.safeParse({
        identifier: value.identifier,
        password: value.password,
      });

      if (!validationResult.success) {
        const errors = validationResult.error.flatten();
        if (errors.formErrors?.[0]) {
          toast.error(errors.formErrors[0]);
        }
        return;
      }

      setIsLoading(true);

      try {
        // On native (Capacitor): use single-request native login — no cookies needed.
        // On web: use standard better-auth login with cookies.
        if (isNativePlatform()) {
          const baseUrl = getBackendUrl();
          const res = await fetch(`${baseUrl}/api-keys/native-login`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ identifier: value.identifier, password: value.password }),
          });

          if (!res.ok) {
            const err = await res.json().catch(() => ({ message: t('login.signInError') }));
            toast.error(err.message || err.error || t('login.signInError'), {
              description: t('login.signInErrorDescription'),
            });
            return;
          }

          const { user, apiKey } = await res.json();
          if (apiKey && user) {
            setDeviceToken(apiKey);
            // Set user directly in auth context — triggers isAuthenticated=true
            // which causes the <Navigate to="/home" /> to render below
            setNativeUser(user, apiKey);
          }

          toast.success(t('login.signInSuccess'), { description: 'Redirecting...' });
          // Don't call navigate() — let the <Navigate> component below handle it
          // when isAuthenticated becomes true from setNativeUser
        } else {
          // Web: standard better-auth login (cookies)
          const authClient = createDynamicAuthClient();
          const isEmail = value.identifier.includes('@');
          const result = isEmail
            ? await authClient.signIn.email({
                email: value.identifier,
                password: value.password,
              })
            : await authClient.signIn.username({
                username: value.identifier,
                password: value.password,
              });

          if (result.error) {
            const errorMessage = result.error.message || t('login.signInError');
            toast.error(errorMessage, {
              description: t('login.signInErrorDescription'),
            });
            return;
          }

          toast.success(t('login.signInSuccess'), { description: 'Redirecting...' });
          window.location.href = '/home';
        }
      } catch (_err) {
        toast.error('An unexpected error occurred. Please try again.');
      } finally {
        setIsLoading(false);
      }
    },
  });

  // Test backend URL connectivity
  const handleTestBackend = async () => {
    if (!tenantIdInput.trim()) {
      setBackendValidationResult(null);
      return;
    }

    let url: string;
    if (isAdvancedMode) {
      // In advanced mode, use the input as full URL
      url = tenantIdInput.trim().replace(/\/$/, '');
    } else {
      // In tenant mode, build URL from tenant ID
      url = buildBackendUrlFromTenant(tenantIdInput.trim());
    }

    setIsValidatingBackend(true);
    setBackendValidationResult(null);

    try {
      const healthCheckUrl = `${url}/api/hello`;
      const response = await fetch(healthCheckUrl, {
        method: 'GET',
        signal: AbortSignal.timeout(5000),
      });

      if (response.ok) {
        setBackendValidationResult('success');
        toast.success('Backend connection successful', {
          description: 'Click "Save & Connect" to apply the configuration.',
        });
      } else {
        setBackendValidationResult('error');
        toast.error('Backend is not responding', {
          description: 'Please check your configuration and try again.',
        });
      }
    } catch {
      setBackendValidationResult('error');
      toast.error('Cannot connect to backend', {
        description: 'Please check your configuration and try again.',
      });
    } finally {
      setIsValidatingBackend(false);
    }
  };

  // Save tenant/backend URL and reload to apply changes
  const handleSaveBackendUrl = () => {
    if (backendValidationResult !== 'success') {
      toast.error('Please test the backend connection first');
      return;
    }

    const url = isAdvancedMode
      ? tenantIdInput.trim().replace(/\/$/, '')
      : buildBackendUrlFromTenant(tenantIdInput.trim());

    // Use setBackendUrl which handles localStorage and triggers page reload
    // This ensures all clients are re-initialized with the new backend URL
    setBackendUrl(url);
  };

  // Clear saved tenant/backend configuration
  const handleClearBackendUrl = () => {
    clearBackendUrl();
    clearTenantId();
    setTenantIdInput('');
    setSavedTenantId('');
    setBackendValidationResult(null);
    toast.success('Configuration cleared', {
      description: 'Using default backend. The page will reload.',
    });
  };

  if (isAuthenticated) {
    return <Navigate to="/home" />;
  }

  const { theme } = useTheme();
  const logoSrc =
    theme === 'dark'
      ? '/favicon-dark/android-chrome-192x192.png'
      : '/favicon/android-chrome-192x192.png';

  return (
    <AuthPage
      title="Sign in"
      description="Enter your credentials to access your account."
      logoSrc={logoSrc}
    >
      <form
        id="login-form"
        className="space-y-6"
        onSubmit={(e) => {
          e.preventDefault();
          form.handleSubmit();
        }}
      >
        <FieldGroup className="space-y-4">
          <form.Field
            name="identifier"
            validators={{
              onBlur: ({ value }) => {
                const result = loginSchema.shape.identifier.safeParse(value);
                return result.success ? undefined : result.error.issues[0]?.message;
              },
              onChange: ({ value }) => {
                const result = loginSchema.shape.identifier.safeParse(value);
                return result.success ? undefined : result.error.issues[0]?.message;
              },
            }}
            children={(field) => {
              const isInvalid = field.state.meta.isTouched && !field.state.meta.isValid;
              return (
                <Field data-invalid={isInvalid}>
                  <FieldLabel htmlFor={field.name}>Username or Email</FieldLabel>
                  <Input
                    id={field.name}
                    name={field.name}
                    type="text"
                    autoComplete="username"
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                    aria-invalid={isInvalid}
                    placeholder="e.g. badge number or name@example.com"
                    disabled={isLoading}
                  />
                  {isInvalid && field.state.meta.errors && (
                    <FieldError errors={field.state.meta.errors} />
                  )}
                </Field>
              );
            }}
          />
          <form.Field
            name="password"
            validators={{
              onBlur: ({ value }) => {
                const result = loginSchema.shape.password.safeParse(value);
                return result.success ? undefined : result.error.issues[0]?.message;
              },
              onChange: ({ value }) => {
                const result = loginSchema.shape.password.safeParse(value);
                return result.success ? undefined : result.error.issues[0]?.message;
              },
            }}
            children={(field) => {
              const isInvalid = field.state.meta.isTouched && !field.state.meta.isValid;
              return (
                <Field data-invalid={isInvalid}>
                  <FieldLabel htmlFor={field.name}>Password</FieldLabel>
                  <Input
                    id={field.name}
                    name={field.name}
                    type="password"
                    autoComplete="current-password"
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                    aria-invalid={isInvalid}
                    placeholder="Enter your password"
                    disabled={isLoading}
                  />
                  {isInvalid && field.state.meta.errors && (
                    <FieldError errors={field.state.meta.errors} />
                  )}
                </Field>
              );
            }}
          />
        </FieldGroup>

        {/* Tenant ID / Backend Configuration */}
        <div className="border-t border-muted-foreground pt-4">
          <details className="group">
            <summary className="flex cursor-pointer items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground list-none">
              <Info className="h-4 w-4" />
              <span>Tenant Configuration</span>
            </summary>
            <div className="mt-4 space-y-3">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setIsAdvancedMode(false);
                    setBackendValidationResult(null);
                  }}
                  className={`text-sm font-medium px-3 py-1 rounded-md transition-colors ${
                    !isAdvancedMode
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                  }`}
                >
                  Tenant ID
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setIsAdvancedMode(true);
                    setBackendValidationResult(null);
                  }}
                  className={`text-sm font-medium px-3 py-1 rounded-md transition-colors ${
                    isAdvancedMode
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                  }`}
                >
                  Advanced (Full URL)
                </button>
              </div>

              {!isAdvancedMode ? (
                <>
                  <p className="text-xs text-muted-foreground">
                    Enter your organization's Tenant ID to connect to your backend server.
                  </p>
                  <div>
                    <FieldLabel htmlFor="tenant-id-input">Tenant ID</FieldLabel>
                    <div className="flex gap-2 mt-1">
                      <Input
                        id="tenant-id-input"
                        type="text"
                        value={tenantIdInput}
                        onChange={(e) => {
                          setTenantIdInput(e.target.value);
                          setBackendValidationResult(null);
                        }}
                        placeholder="e.g. demo, icbp, trias"
                        disabled={isValidatingBackend || isLoading}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={handleTestBackend}
                        disabled={!tenantIdInput.trim() || isValidatingBackend || isLoading}
                        title="Test connection"
                      >
                        {isValidatingBackend ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : backendValidationResult === 'success' ? (
                          <Check className="h-4 w-4 text-green-500" />
                        ) : backendValidationResult === 'error' ? (
                          <X className="h-4 w-4 text-red-500" />
                        ) : (
                          <Info className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <p className="text-xs text-muted-foreground">
                    Enter the full backend URL. Use this option if your server uses a custom domain.
                  </p>
                  <div>
                    <FieldLabel htmlFor="backend-url-input">Backend URL</FieldLabel>
                    <div className="flex gap-2 mt-1">
                      <Input
                        id="backend-url-input"
                        type="url"
                        value={tenantIdInput}
                        onChange={(e) => {
                          setTenantIdInput(e.target.value);
                          setBackendValidationResult(null);
                        }}
                        placeholder="https://your-server.workers.dev"
                        disabled={isValidatingBackend || isLoading}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        onClick={handleTestBackend}
                        disabled={!tenantIdInput.trim() || isValidatingBackend || isLoading}
                        title="Test connection"
                      >
                        {isValidatingBackend ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : backendValidationResult === 'success' ? (
                          <Check className="h-4 w-4 text-green-500" />
                        ) : backendValidationResult === 'error' ? (
                          <X className="h-4 w-4 text-red-500" />
                        ) : (
                          <Info className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                </>
              )}

              {savedTenantId && !isAdvancedMode && savedTenantId !== tenantIdInput && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground bg-muted p-2 rounded">
                  <Info className="h-4 w-4" />
                  <span>
                    Current tenant: <span className="font-medium">{savedTenantId}</span>
                  </span>
                </div>
              )}

              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleSaveBackendUrl}
                  disabled={
                    backendValidationResult !== 'success' ||
                    (isAdvancedMode ? false : tenantIdInput.trim() === savedTenantId) ||
                    isLoading
                  }
                >
                  Save & Connect
                </Button>
                {savedTenantId && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={handleClearBackendUrl}
                    disabled={isLoading}
                  >
                    Clear
                  </Button>
                )}
              </div>
            </div>
          </details>
        </div>

        <FieldGroup className="pt-2 space-y-4">
          <Field>
            <Button
              type="submit"
              form="login-form"
              disabled={isLoading}
              className="w-full"
              size="lg"
            >
              {isLoading ? 'Signing in...' : 'Sign in'}
            </Button>
          </Field>
          <Field className="text-center">
            <p className="text-sm text-muted-foreground">
              Don't have an account?{' '}
              <Link to="/register" className="text-sm font-medium text-primary hover:underline">
                Sign up
              </Link>
            </p>
          </Field>
        </FieldGroup>
      </form>
    </AuthPage>
  );
}
